<template>

  <fieldset class="card contacts">

    <the-spinner v-if="state === 'LOAD'"/>

    <div v-if="state === 'ERR'"><p class="text-danger">{{ $t('adm.error.crmGeneric') }}</p></div>

    <div v-if="state === 'RDY' && crmContacts" class="contacts-container">

      <div
          id="warnContactDeleted"
          class="alert alert-warning"
          v-text="$t('adm.page.sync.warnContactDeleted')"
          v-if="warnContactDeleted"/>

      <data-table
          table-id="contacts-table"
          class="table table-borderless table-hover no-margin"
          :columns="crmContactColumns"
          :items="crmContacts"
          :show-headers="false"
          :is-item-active="isContactActive"
          @item-click="updateSelection($event)"
          :caption="tableCaption">

        <template v-slot:select="{ item, index }">
          <input
              type="radio"
              class="form-check-input"
              name="match"
              v-model="selected"
              :value="item.crmId"
              @click="updateSelection(index)"
              :disabled="item.inactive"
              aria-label="select contact">
        </template>

        <template v-slot:organization="{ item }">
          <a href="#" @click="$emit('organizationClicked', item.orgId)" v-text="item.orgName"/>
        </template>

        <template v-slot:crmId="{ item }">
          <a
              class="link"
              :href="crmLink(item.crmId)"
              target="_blank"
              rel="noopener noreferrer"
              :title="$t('adm.page.sync.gotoCRM')">
            <span class="d-inline-block">{{ item.crmId }}</span>
            <img
                src="@/assets/img/icons/status-warning.svg"
                v-if="item.inactive"
                :alt="$t('adm.label.inactiveContact')"
                :title="$t('adm.label.inactiveContact')"/>
          </a>
        </template>

      </data-table>

    </div>
  </fieldset>

</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import DataTable, { TableColumns, TableRow } from '@/components/ui/DataTable.vue';
import TheSpinner from '@/components/ui/TheSpinner.vue';
import Backend, { CrmContact, UserData } from '@/components/backend';
import { Logger } from 'oidc-client-ts';
import { arraySize } from '@/components/utils';

const searchMatchingCRMContacts = async (email: string): Promise<CrmContact[]> => {
  Logger.debug('sync:contacts', `loading crm-contacts for ${email}...`);

  const api = await Backend.validateToken();
  return await api.searchCrmContacts({ email });
};

export default defineComponent({
  name: 'ContactsTable',
  components: { TheSpinner, DataTable },
  emits: ['contactSelected', 'organizationClicked'],
  props: {
    initSelectedContact: { type: String, default: null }, // crm-id of the selected contact
    user: { type: Object as PropType<UserData>, required: true },
  },
  data() {
    return {
      selected: this.initSelectedContact,
      crmContacts: [] as CrmContact[],
      state: 'LOAD' as 'LOAD' | 'RDY' | 'ERR',
    };
  },
  computed: {
    crmContactColumns(): TableColumns {
      return {
        select: { label: '', selfRender: true },
        email: { label: this.$t('adm.label.email') },
        firstname: { label: this.$t('adm.label.firstname') },
        lastname: { label: this.$t('adm.label.lastname') },
        organization: { label: this.$t('adm.label.organization'), selfRender: true },
        crmNo: { label: this.$t('adm.label.crmNo') },
        crmId: { label: this.$t('adm.label.crmId'), itemClass: 'fs-6', selfRender: true },
      };
    },
    warnContactDeleted(): boolean {
      // const selCrmId = this.user.crmId;
      // Logger.debug("[sync] selCrmId", selCrmId, " contacts:", this.crmContacts)
      return this.state === 'RDY' && !!this.contactById(this.user.crmId)?.inactive;
      // && !!this.crmContacts.find((c: CrmContact) => c.crmId === selCrmId)?.inactive;
    },
    tableCaption() {
      return arraySize(this.crmContacts) === 0 ? this.$t('adm.page.sync.noMatch') : undefined;
    },
  },
  methods: {
    contactById(crmId?: string): CrmContact | undefined {
      return this.crmContacts?.find((c: CrmContact) => c.crmId === crmId);
    },
    updateSelection(idx: number) {
      const match = idx >= 0 && idx < this.crmContacts.length ? this.crmContacts[idx] : undefined;
      this.selectContact(match);
      //Logger.debug('sync:contacts', 'updateSelection:', match, ' index:', idx);
    },
    selectContact(contact?: CrmContact) {
      if (contact && !contact.inactive) {
        Logger.debug('sync:contacts', 'selectContact:', contact);
        this.selected = contact.crmId;
        this.$emit('contactSelected', contact);
      }
    },
    isContactActive(c: TableRow): boolean {
      return !(c as CrmContact).inactive;
    },
  },
  beforeMount() {
    Logger.info('sync:contacts', 'mounting');
    searchMatchingCRMContacts(this.user.email).then((contacts) => {
      this.crmContacts = contacts;
      this.selectContact(this.contactById(this.initSelectedContact));
      this.state = 'RDY';
    }).catch(e => {
      Logger.error('sync:contacts', 'failed to load crm-contacts', e);
      this.state = 'ERR';
    });
  },
});
</script>

<style>

.card.contacts {
  --bs-card-border-color: var(--grey-mid);
  --bs-card-spacer-y: 1.5rem;

  padding: var(--bs-card-spacer-y) var(--bs-card-spacer-x);
}

.contacts-container {
  max-height: 30rem;
  overflow: auto;
}

#contacts-table {
  --stihl-table-filter: none;
}

#contacts-table tbody tr.is-disabled {
  --bs-table-bg: var(--grey-light);
  --bs-table-hover-bg: var(--grey-light);
}

#contacts-table tbody td:first-child {
  --bs-table-bg: white;
  --bs-table-hover-bg: white;
  border: 0;
}

#contacts-table tbody td {
  border: 1px solid var(--grey-mid);
  border-top: 0;
  border-left: 0;
}

#contacts-table .link-with-icon {
  display: flex;
  gap: .5rem;
}

</style>
